/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable react/jsx-key */
import {
  forwardRef,
  useEffect,
  useImperativeHandle
} from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "@chakra-ui/icons";
import {
  Column,
  useTable,
  usePagination,
  useSortBy,
  Row,
  useFilters
} from "react-table";
import {
  Box,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button
} from "@chakra-ui/react";

interface TableProps<T extends object> {
  columns: Array<Column<T>>
  data: readonly T[];
  pageCount: number;
  fetchData: (pageIndex: number, pageSize: number) => void;
  loading: boolean;
  handleClick?: (row: Row<T>) => void;
}

export interface ITableInstance {
  reloadTable: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TableComponent: React.ForwardRefRenderFunction<unknown, TableProps<any>> = (
  {
    columns,
    data,
    fetchData,
    handleClick,
    pageCount: controlledPageCount
  }, ref) => {
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize }
  } = useTable({
    columns,
    manualPagination: true,
    pageCount: controlledPageCount,
    autoResetPage: false,
    data
  },
  useFilters,
  useSortBy,
  usePagination
  );

  useEffect(() => {
    fetchData(
      pageIndex,
      pageSize
    );
  }, [
    fetchData,
    pageIndex,
    pageSize
  ]);

  useImperativeHandle(ref, () => ({
    reloadTable() {
      fetchData(
        pageIndex,
        pageSize
      );
    }
  }));

  // Filter handler for filtering by partner

  // Render the UI for your table
  return (
    <Box>
      <ChakraTable
        variant="striped"
        {...getTableProps()}
      >
        <Thead>
          {headerGroups.map(headerGroup => (
            <Tr
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map(column => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  onClick={column.Header === "Date" ? () => column.toggleSortBy(!column.isSortedDesc) : undefined}
                >
                  {column.render("Header")}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>

        {rows.length ? (
          <Tbody
            {...getTableBodyProps()}

          >
            {rows.map(row => {
              prepareRow(row);

              return (
                <Tr
                  cursor={handleClick ? "pointer" : "auto"}
                  {...row.getRowProps()}
                  onClick={() => handleClick && handleClick(row)}
                >
                  {row.cells.map(cell => {
                    return (
                      <Td
                        {...cell.getCellProps()}
                        style={{ verticalAlign: "middle" }}
                      >
                        {cell.render("Cell")}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>

        ) : (
          <Tbody>
            <Tr>
              <Td
                textAlign="center"
                colSpan={columns.length}
              >
                No data to show
              </Td>
            </Tr>
          </Tbody>
        )}
      </ChakraTable>

      <Box py={5}>
        <Button
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
        >
          <ArrowLeftIcon
            w={4}
            h={4}
          />
        </Button>

        {" "}

        <Button
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          <ChevronLeftIcon
            w={8}
            h={8}
          />
        </Button>

        {" "}

        <Button
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          <ChevronRightIcon
            w={8}
            h={8}
          />
        </Button>

        {" "}

        <Button
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
        >
          <ArrowRightIcon
            w={4}
            h={4}
          />
        </Button>

        {" "}

        <span>
          Page
          {" "}

          <strong>
            {pageIndex + 1}

            {" "}
            of

            {" "}

            {pageOptions.length}
          </strong>

          {" "}
        </span>
      </Box>
    </Box>
  );
};

export const Table = forwardRef(TableComponent);
